/**
 * BewirtungSection — Pflichtfelder nach § 4 Abs. 5 Nr. 2 EStG für
 * Bewirtungsbelege (Baustellenversorgung etc.).
 *
 * Inline-styled (Tailwind ist im scanflow-mobile-Workspace nicht installiert).
 * Logik 1:1 aus Base44 fresh-scan-flow-vault/src/components/beleg/BewirtungSection.jsx
 *
 * Pflichtangaben:
 *   - Anlass (Mittag / Geschäftsessen / Freitext)
 *   - Mindestens ein interner Teilnehmer (Inhaber/Helfer aus dem Team)
 *   - Optional externe Teilnehmer (Komma-separiert)
 *   - Trinkgeld (optional, ist in der Buchung relevant)
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, Users, Loader2, UtensilsCrossed } from "lucide-react";
import { api, type TeamMember } from "../../lib/api";

type Form = {
  bewirtung_anlass: string;
  bewirtung_teilnehmer_intern: string[];
  bewirtung_teilnehmer_extern: string[];
  bewirtung_trinkgeld: number;
};

type Props = {
  value: Form;
  onChange: (next: Form) => void;
};

const ANLASS_OPTIONEN = [
  { value: "Mittag", label: "Mittag" },
  { value: "Geschäftsessen", label: "Geschäftsessen" },
];

const C = {
  border: "#f6a45c", // orange-300
  bg: "rgba(254, 215, 170, 0.15)", // orange-50 (auf dunklem Hintergrund weicher)
  bgInset: "rgba(254, 215, 170, 0.25)",
  ink: "#9a3412", // orange-800
  inkSoft: "#c2410c", // orange-700
  inkLight: "rgba(154, 52, 18, 0.7)",
  active: "#ea580c", // orange-600
  activeHover: "#c2410c",
};

export default function BewirtungSection({ value, onChange }: Props) {
  const [externInput, setExternInput] = useState("");

  const teamQ = useQuery({
    queryKey: ["scanflow-team"],
    queryFn: () => api.listTeam(),
    staleTime: 60 * 60 * 1000, // 1h
  });

  function toggleIntern(memberId: string) {
    const current = value.bewirtung_teilnehmer_intern;
    if (current.includes(memberId)) {
      onChange({ ...value, bewirtung_teilnehmer_intern: current.filter((id) => id !== memberId) });
    } else {
      onChange({ ...value, bewirtung_teilnehmer_intern: [...current, memberId] });
    }
  }

  function addExtern() {
    const names = externInput.split(",").map((s) => s.trim()).filter(Boolean);
    if (!names.length) return;
    const merged = Array.from(new Set([...value.bewirtung_teilnehmer_extern, ...names]));
    onChange({ ...value, bewirtung_teilnehmer_extern: merged });
    setExternInput("");
  }

  function removeExtern(name: string) {
    onChange({
      ...value,
      bewirtung_teilnehmer_extern: value.bewirtung_teilnehmer_extern.filter((n) => n !== name),
    });
  }

  return (
    <div
      style={{
        borderRadius: 14,
        border: `1px solid ${C.border}`,
        background: C.bg,
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <UtensilsCrossed size={18} color={C.active} />
        <span style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>
          Bewirtungsbeleg
        </span>
      </div>

      {/* Anlass */}
      <div>
        <Label>Anlass der Bewirtung *</Label>
        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          {ANLASS_OPTIONEN.map((opt) => {
            const active = value.bewirtung_anlass === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange({ ...value, bewirtung_anlass: opt.value })}
                style={{
                  flex: 1,
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: `1px solid ${active ? C.active : C.border}`,
                  background: active ? C.active : "white",
                  color: active ? "white" : C.ink,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <input
          type="text"
          value={
            value.bewirtung_anlass &&
            !ANLASS_OPTIONEN.find((o) => o.value === value.bewirtung_anlass)
              ? value.bewirtung_anlass
              : ""
          }
          onChange={(e) => onChange({ ...value, bewirtung_anlass: e.target.value })}
          placeholder="oder eigenen Anlass eingeben…"
          style={{
            marginTop: 8,
            width: "100%",
            boxSizing: "border-box",
            padding: "8px 12px",
            borderRadius: 8,
            border: `1px solid ${C.border}`,
            background: "white",
            color: C.ink,
            fontSize: 13,
          }}
        />
      </div>

      {/* Trinkgeld */}
      <div>
        <Label>Trinkgeld (EUR)</Label>
        <input
          type="number"
          step="0.50"
          min="0"
          value={value.bewirtung_trinkgeld}
          onChange={(e) =>
            onChange({ ...value, bewirtung_trinkgeld: parseFloat(e.target.value) || 0 })
          }
          style={{
            marginTop: 6,
            width: "100%",
            boxSizing: "border-box",
            padding: "10px 12px",
            borderRadius: 8,
            border: `1px solid ${C.border}`,
            background: "white",
            color: C.ink,
            fontSize: 14,
          }}
        />
      </div>

      {/* Interne Teilnehmer */}
      <div>
        <Label>
          <Users size={13} style={{ marginRight: 4, verticalAlign: -2 }} />
          Teilnehmer (Team) *
        </Label>
        {teamQ.isLoading && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, fontSize: 12, color: C.inkLight }}>
            <Loader2 size={14} className="m-spin" /> Lade Team…
          </div>
        )}
        {teamQ.isError && (
          <div style={{ marginTop: 6, fontSize: 12, color: "tomato" }}>
            Team-Liste konnte nicht geladen werden.
          </div>
        )}
        {teamQ.data && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
            {teamQ.data.map((m: TeamMember) => {
              const selected = value.bewirtung_teilnehmer_intern.includes(m.id);
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => toggleIntern(m.id)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 999,
                    border: `1px solid ${selected ? C.active : C.border}`,
                    background: selected ? C.active : "white",
                    color: selected ? "white" : C.ink,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {m.name}
                  {m.role === "inhaber" && (
                    <span style={{ marginLeft: 4, opacity: 0.6 }}>★</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Externe Teilnehmer */}
      <div>
        <Label>Externe Teilnehmer</Label>
        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
          <input
            type="text"
            value={externInput}
            onChange={(e) => setExternInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault();
                addExtern();
              }
            }}
            placeholder="Name eingeben, Enter…"
            style={{
              flex: 1,
              padding: "8px 12px",
              borderRadius: 8,
              border: `1px solid ${C.border}`,
              background: "white",
              color: C.ink,
              fontSize: 13,
            }}
          />
          <button
            type="button"
            onClick={addExtern}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: "none",
              background: C.active,
              color: "white",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            +
          </button>
        </div>
        {value.bewirtung_teilnehmer_extern.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
            {value.bewirtung_teilnehmer_extern.map((name) => (
              <span
                key={name}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "4px 10px",
                  borderRadius: 999,
                  background: "white",
                  border: `1px solid ${C.border}`,
                  color: C.ink,
                  fontSize: 12,
                }}
              >
                {name}
                <span style={{ opacity: 0.5, fontSize: 10 }}>(extern)</span>
                <button
                  type="button"
                  onClick={() => removeExtern(name)}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: C.ink,
                    cursor: "pointer",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                  }}
                  aria-label={`${name} entfernen`}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: C.inkSoft,
        textTransform: "uppercase",
        letterSpacing: 0.5,
      }}
    >
      {children}
    </div>
  );
}
